import React, { useState } from 'react';
import axiosInstance from '@/lib/axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Mail, Phone, Briefcase, User as UserIcon, Download, Check, X } from 'lucide-react';
import { formatDisplayPhone } from '@/lib/phone-utils';
import type { UserWithCommunities } from '@/types';
import type { LeadPostData } from '../../types';
import { MentionText } from '@/components/mentions/MentionText';
import parseFileName from './FileNameParser';
import { OpenLeadInterestedList } from './OpenLeadInterestedList';

interface LeadPostContentProps {
  post: LeadPostData;
  user: UserWithCommunities | undefined;
}

const getDownloadUrl = (url: string) => {
  if (!url) return '';
  if (url.includes('res.cloudinary.com') && !url.includes('fl_attachment')) {
    return url.replace('/upload/', '/upload/fl_attachment/');
  }
  return url;
};

const trackStatuses = [
  'Not Contacted Yet',
  'Contacted',
  'No Response',
  'Got The Business',
  'Did Not Get The Business',
  'Not a Good Fit',
  'Confidential',
];

export function LeadPostContent({ post, user }: LeadPostContentProps) {
  const isDirect = !!post.toUserId || !!post.toBusinessId;
  const isAuthor = post.authorId === user?.id;
  const isLeadUser = isDirect ? post.toUserId === user?.id : false;
  const canMarkInterested = (isLeadUser || !isDirect) && !isAuthor;

  const [isInterested, setIsInterested] = useState(!!post.isInterested);
  const [isTrackLoading, setIsTrackLoading] = useState(false);
  const [selectedTrackStatus, setSelectedTrackStatus] = useState<string>(
    post.status || 'Not Contacted Yet'
  );

  const handleInterestedClick = async () => {
    const newInterested = !isInterested;
    setIsInterested(newInterested);

    try {
      if (newInterested) {
        setIsTrackLoading(true);
      }

      await axiosInstance.post(`/api/leads/${post.leadId}/track`, {
        isInterested: newInterested,
      });
    } catch (error) {
      console.error('Failed to track lead interested', error);
      if (!newInterested) setIsInterested(false);
    } finally {
      setIsTrackLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    setSelectedTrackStatus(newStatus);
    try {
      setIsTrackLoading(true);
      await axiosInstance.post(`/api/leads/${post.leadId}/track`, {
        status: newStatus,
      });
    } catch (error) {
      console.error('Track lead error:', error);
      if (!isInterested) setIsInterested(false);
    } finally {
      setIsTrackLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-lg">{post.leadTitle}</h3>
            {(post.leadRecipientFirstName ||
              post.leadRecipientLastName ||
              post.leadRecipientBusinessName) && (
              <p className="text-xs text-muted-foreground mt-2">
                To:{' '}
                <span className="text-lg text-muted-foreground font-bold">
                  {post.leadRecipientFirstName || post.leadRecipientLastName
                    ? `${post.leadRecipientFirstName ?? ''} ${post.leadRecipientLastName ?? ''}`.trim()
                    : post.leadRecipientBusinessName?.trim()}
                </span>
              </p>
            )}
            {post.leadTimeline ? (
              <Badge variant="outline" className="mt-1">
                Required: {post.leadTimeline}
              </Badge>
            ) : null}
          </div>
          <Badge variant={!isDirect ? 'secondary' : 'default'} className="!whitespace-nowrap">
            {!isDirect ? 'Open Lead' : 'Direct Lead'}
          </Badge>
        </div>
      </div>
      {post.leadImage && (
        <img
          src={post.leadImage}
          className="w-full aspect-video  max-h-80 object-contain rounded-xl"
          alt="Lead"
        />
      )}
      <p className="whitespace-pre-wrap text-sm text-foreground/90 bg-muted/30 p-4 rounded-md border-2 border-black/15 dark:border-white/15">
        <MentionText text={post.leadDescription || ''} />
      </p>
      {post.leadAttachments && post.leadAttachments.length > 0 && (
        <>
          <h4 className="text-sm font-semibold text-foreground/80 mb-2 border-t border-border/50 pt-2">
            Attachments
          </h4>
          <div className="flex flex-col gap-3">
            {post.leadAttachments.map((attachment, index) => (
              <a
                key={index}
                href={getDownloadUrl(attachment)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between w-full p-3 rounded-md border-2 border-black/15 dark:border-white/15 bg-muted/30 hover:bg-muted/50 hover:border-black/25 dark:hover:border-white/25 transition-colors group"
                download
              >
                <div className="flex items-center gap-2 flex-1 min-w-0 mr-4">
                  <span className="text-sm font-medium text-foreground/90 truncate">
                    {parseFileName(attachment)}
                  </span>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-8 px-2.5 sm:px-3 shadow-sm"
                  asChild
                >
                  <div className="cursor-pointer pointer-events-none flex items-center justify-center">
                    <Download className="w-4 h-4 sm:w-3.5 sm:h-3.5 sm:mr-2" />
                    <span className="hidden sm:inline text-xs">Download</span>
                  </div>
                </Button>
              </a>
            ))}
          </div>
        </>
      )}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {canMarkInterested &&
          (isInterested ? (
            <Button
              variant="secondary"
              className="w-full sm:w-[160px] rounded-full bg-secondary/40 hover:bg-secondary/60 flex items-center justify-between pl-4 pr-1.5 py-5 border border-primary/20 transition-all group shadow-sm"
              onClick={handleInterestedClick}
              disabled={isTrackLoading}
            >
              {isTrackLoading ? (
                <span className="text-foreground w-full text-center text-sm">Loading...</span>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-primary">
                    <Check className="w-5 h-5 font-bold" />
                    <span className="font-semibold text-base py-0.5">Interested</span>
                  </div>
                  <div className="flex items-center justify-center p-1 rounded-full hover:bg-black/10 dark:hover:bg-white/10 text-muted-foreground transition-colors ml-1">
                    <X className="w-4 h-4" />
                  </div>
                </>
              )}
            </Button>
          ) : (
            <Button
              variant="default"
              className="w-full sm:w-[160px] rounded-full py-5 text-base font-semibold transition-all shadow-sm"
              onClick={handleInterestedClick}
              disabled={isTrackLoading}
            >
              {isTrackLoading ? 'Loading...' : 'Interested'}
            </Button>
          ))}
        {isInterested && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="w-full sm:w-auto min-w-[200px] justify-between"
                disabled={isTrackLoading}
              >
                {selectedTrackStatus}
                <span className="ml-2 opacity-50 text-xs">▼</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[200px]">
              {trackStatuses.map((status) => (
                <DropdownMenuItem
                  key={status}
                  onClick={() => handleStatusChange(status)}
                  className="flex items-center justify-between"
                >
                  {status}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {isAuthor && !isDirect && <OpenLeadInterestedList leadId={post.leadId} />}

      {isInterested && (
        <div className="mt-4 p-4 rounded-xl bg-accent/30 border border-border/50 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
          <h4 className="text-sm font-semibold text-foreground/80 mb-2 border-b border-border/50 pb-2">
            Contact Information
          </h4>

          {post.leadType === 'SELF' ? (
            <div className="space-y-2">
              {user?.email && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <Mail className="w-4 h-4 mr-2 text-primary/70" />
                  <a href={`mailto:${user.email}`} className="hover:text-primary transition-colors">
                    {user.email}
                  </a>
                </div>
              )}
              {user?.phone && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <Phone className="w-4 h-4 mr-2 text-primary/70" />
                  <a href={`tel:${user.phone}`} className="hover:text-primary transition-colors">
                    {formatDisplayPhone(user.phone)}
                  </a>
                </div>
              )}
              {!user?.email && !user?.phone && (
                <p className="text-sm text-muted-foreground italic">
                  No contact details available.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {post.personName && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <UserIcon className="w-4 h-4 mr-2 text-primary/70" />
                  <span className="font-medium text-foreground/90">{post.personName}</span>
                </div>
              )}
              {post.email && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <Mail className="w-4 h-4 mr-2 text-primary/70" />
                  <a href={`mailto:${post.email}`} className="hover:text-primary transition-colors">
                    {post.email}
                  </a>
                </div>
              )}
              {post.contactNumber && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <Phone className="w-4 h-4 mr-2 text-primary/70" />
                  <a
                    href={`tel:${post.contactNumber}`}
                    className="hover:text-primary transition-colors"
                  >
                    {formatDisplayPhone(post.contactNumber)}
                  </a>
                </div>
              )}
              {!post.personName && !post.email && !post.contactNumber && (
                <p className="text-sm text-muted-foreground italic">
                  No contact details available.
                </p>
              )}
            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Briefcase className="w-3 h-3" />
        {post.leadType === 'SELF' ? 'For Self' : 'For Someone Else'}
      </div>
    </div>
  );
}
